import { firstValueFrom } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import connectRange, {
    RangeConnectorParams as CParams,
    RangeWidgetDescription as WDescription,
} from 'instantsearch.js/es/connectors/range/connectRange'
import { Renderer } from 'instantsearch.js/es/types'

import { Component, DestroyRef, forwardRef, Inject, Input, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { FormControl } from '@angular/forms'

import { AnalyticsService } from '@app-domains/analytics/analytics.service'
import { Analytics } from '@app-domains/analytics/types/analytics.types'
import { delay } from '@app-lib/common.lib'
import { AbstractInstantsearchWidgetComponent } from '@app-domains/algolia/abstract-instantsearch-widget.component'
import { AlgoliaInstantsearchComponent } from '@app-domains/algolia/components/algolia-instantsearch.component'
import { DimensionUnits } from '@app-content-blocks/product-card/product-card.types'

type State = WDescription['renderState']

@Component({
    selector: 'app-size-range',
    templateUrl: './size-range.component.html',
    styleUrls: ['./size-range.component.scss'],
})
export class SizeRangeComponent extends AbstractInstantsearchWidgetComponent<
WDescription,
CParams
> implements OnInit {

    public override readonly widgetName = 'RangeSlider'

    @Input()
    public attribute: string

    @Input()
    public filterName: 'length' | 'weight' | 'width' | 'height' | 'diameter'

    @Input()
    public unit: string = DimensionUnits.cm

    public control: FormControl

    private filterMax: string
    private filterMin: string

    public minInput: FormControl = new FormControl()
    public maxInput: FormControl = new FormControl()

    public readonly labelFormatter = (v: number): string => Math.round(v).toString()

    constructor(
        @Inject(forwardRef(() => AlgoliaInstantsearchComponent))
        public instantSearchInstance: AlgoliaInstantsearchComponent,
        private analytics: AnalyticsService,
        private route: ActivatedRoute,
        private router: Router,
        private destroy: DestroyRef,
    ) {
        super()
    }

    public override ngOnInit(): void {
        this.filterMax = `${this.filterName}[max]`
        this.filterMin = `${this.filterName}[min]`

        this.minInput.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroy),
                debounceTime(1500),
            ).subscribe((value) => {
                if (value === this.control.value[0]) {
                    return
                }

                this.control.setValue([value, this.control.value[1]])
                this.state?.refine(this.control.value)
                this.setUrlPart()
            })

        this.maxInput.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroy),
                debounceTime(1500),
            )
            .subscribe((value) => {
                if (value === this.control.value[1]) {
                    return
                }

                this.control.setValue([this.control.value[0], value])
                this.state?.refine(this.control.value)
                this.setUrlPart()
            })

        this.createWidget(connectRange, {
            attribute: this.attribute,
            precision: 0,
        })

        super.ngOnInit()
    }

    public override updateState: Renderer<State, CParams> = async (state, isFirstRendering) => {
        if (isFirstRendering) {
            this.state = state
            return
        }

        if (! state.range.min && ! state.range.max) {
            return
        }

        if (! this.control) {
            this.control ??= new FormControl(this.state?.start)
        }

        this.state = state

        if (isFirstRendering) {
            this.minInput.setValue(state.range.min)
            this.maxInput.setValue(state.range.max)
        }

        await this.initialiseRefinementFromUrl()

        // We have to delay the form control value update until the range-slider component
        // has detected any changes in the range as provided by the new state we just set.
        delay(10).then(() => {
            const controlValue = this.control.value

            this.minInput.setValue(controlValue[0])
            this.maxInput.setValue(controlValue[1])

            // We'll also defer instantiating the form control up until this point as to
            // prevent some race conditions with initial state updates. The underlying
            // range slider instance now only instantiates once we have some actual data
            // to work with.
            const filterType: Analytics.FilterTypes = 'slider'
            const filterValueMin = String(state.start[0] === -Infinity ? state.range.min : state.start[0])
            const filterValueMax = String(state.start[1] === Infinity ? state.range.max : state.start[1])

            // Only log when the range has been changed from min and/or max range
            if (filterValueMin !== String(state.range.min)
                || filterValueMax !== String(state.range.max)
            ) {
                this.analytics.logFilterOn(filterType, this.filterName, filterValueMin, filterValueMax)
            }
        })
    }

    private async initialiseRefinementFromUrl(): Promise<void> {
        const queryParams = await firstValueFrom(this.route.queryParamMap)

        const rangeMin = queryParams.get(this.filterMin)
        const rangeMax = queryParams.get(this.filterMax)

        if (rangeMin == null && rangeMax == null) {
            this.resetRange(false)
        }

        if ((rangeMin != null && +rangeMin < this.state!.range!.min!) ||
            (rangeMax != null && +rangeMax > this.state!.range!.max!)
        ) {
            this.resetRange(true)
        }

        if (rangeMin != null && ! isNaN(+rangeMin)) {
            this.state!.start[0] = +rangeMin
            this.control.setValue([+rangeMin, this.control.value[1]])
        }

        if (rangeMax != null && ! isNaN(+rangeMax)) {
            this.state!.start[1] = +rangeMax
            this.control.setValue([this.control.value[0], +rangeMax])
        }

        delay(10).then(() => this.control.setValue(this.state?.start))
        this.state?.refine(this.state?.start)
    }

    public valueChange(): void {
        this.setUrlPart()
        this.state?.refine(this.control.value)
    }

    private resetRange(setUrl: boolean): void {
        this.state!.start[0] = this.state?.range.min
        this.state!.start[1] = this.state?.range.max

        // We have to delay the form control value update until the range-slider component
        // has detected any changes in the range.
        delay(10).then(() => this.control.setValue(this.state?.start))

        if (setUrl) {
            this.setUrlPart()
        }

        return
    }

    private setUrlPart(): void {
        const controlValue = this.control.value

        // Check if the control values are equal to the state's range values
        const isMinDefault = controlValue[0] === this.state?.range.min
        const isMaxDefault = controlValue[1] === this.state?.range.max

        const noValueChange = isMinDefault && isMaxDefault

        void this.router.navigate(
            [],
            {
                relativeTo: this.route,
                queryParams: {
                    [this.filterMin]: noValueChange ? null : controlValue[0],
                    [this.filterMax]: noValueChange ? null : controlValue[1],
                },
                queryParamsHandling: 'merge',
            },
        )
    }

}
